import React, { useMemo, useCallback } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import cn from 'classnames';
import { useNavigate, useParams } from 'react-router-dom';
import { TouchableDiv } from '@components/common/Touchable';
import { APP_CONFIG } from '@constants';
import { urlSearchParams } from '@utility/utills';
import { getPath } from '@containers/OrderItemDetailV2/OrderItemDetailHandler';
import style from './Item.module.scss';

const Item = ({
  modifiersHashTable,
  name,
  onClickHandler,
  weight,
  modifierId,
  serverAppTheme,
  isModifierRequired,
  isLimitOver,
  isActive,
  menusList,
  menuIndex,
  isVerticalLayout,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const { menuTypeId, menuItemId } = params;

  const item = modifiersHashTable[modifierId];

  const tileStyle = useMemo(() => {
    const bgColor = item?.serverAppButtonColor
      ? isActive ? serverAppTheme?.selected : item.serverAppButtonColor
      : isActive ? serverAppTheme?.selected
      : serverAppTheme?.global_mod_tile

    const base = {
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundColor: bgColor,
      wordBreak: 'normal',
      borderRadius: '4px',
      color: 'rgb(0, 0, 0)'
    };

    if (!isVerticalLayout) return base;

    return {
      ...base,
      backgroundImage: item?.serverAppAsImage && item?.intermediateImageUrl
        ? `url(${item.intermediateImageUrl})`
        : '',
      width: '100%',
      minHeight: '54px',
      padding: '4px',
      marginBottom: '4px',
      position: 'relative',
    };
  }, [isActive, isModifierRequired, isVerticalLayout, item, serverAppTheme]);

  const nameStyle = useMemo(() => {
    if (!isVerticalLayout) return undefined;
    return {
      wordBreak: item?.name?.length > 18 ? 'break-word' : 'normal',
      lineHeight: item?.name?.length > 18 ? '1.2' : '',
      color: item?.outOfStock ? '#A0A0A0' : isActive ? 'black' : serverAppTheme?.primary_text,
    };
  }, [isVerticalLayout, item, isActive, serverAppTheme]);

  const handleClick = useCallback(e => {
    
    e.preventDefault();
    const modifier = modifiersHashTable[modifierId];
    if (modifier?.linkType === 1) {
      const parentMenuItemId = urlSearchParams('parentMenuItemId') || menuItemId;
      const menuId = menusList[menuIndex]?.id;
      navigate(
        `${APP_CONFIG.BASE_URL}/order-item/${menuTypeId}/${modifierId}?menuId=${menuId}&parentMenuItemId=${parentMenuItemId}`
      );
      return;
    }
    onClickHandler(e, !isActive, { weight, modifierId }, isLimitOver);
  }, [modifiersHashTable, modifierId, menuItemId, menuTypeId, menusList, menuIndex, isActive, weight, isLimitOver, onClickHandler, navigate]);

  return (
    <TouchableDiv
      id={modifierId}
      className={cn(
        isVerticalLayout
          ? cn(
              'p-1 position-relative mb-1',
              isActive ? style.activeModifier : isModifierRequired ? style.requiredModifier : style.optionalModifier,
              item?.outOfStock ? style.opacity0 : ''
            )
          : cn('fs-14 fw-100 d-flex align-items-start text-truncate', style.modifier)
      )}
      style={tileStyle}
      onClick={handleClick}
    >
      {isVerticalLayout ? (
        !(item?.serverAppAsImage && item?.intermediateImageUrl) && (
          <div
            className={cn(
              'col-12 px-0 fw-100',
              item?.name?.length < 14 ? 'fs_12' : 'fs_14',
              style.modifierName
            )}
            style={nameStyle}
          >
            {name}
          </div>
        )
      ) : (
        <div className={style.modifierNameTruncate}>{name}</div>
      )}
    </TouchableDiv>
  );
};

const mapStateToProps = ({ orders, theme }, ownProps) => {
  const fullPath = getPath(
    orders.nestedModifierArray,
    `${ownProps.modifierId}-${ownProps.modifierGroupId}`
  );
  // In global modifier mode the path is stored with selectedModifierPath as prefix
  const globalModPath =
    orders.globalSelectedModifierId && orders.selectedModifierPath
      ? `${orders.selectedModifierPath}|${ownProps.modifierId}-${ownProps.modifierGroupId}`
      : null;
  return {
    modifiersHashTable: orders.modifiersHashTable,
    serverAppTheme: theme.serverAppTheme,
    isActive:
      orders.modifierData?.some(
        m => m === fullPath || m.startsWith(`${fullPath}|`)
      ) ||
      Boolean(
        globalModPath &&
          orders.modifierData?.some(
            m => m === globalModPath || m.startsWith(`${globalModPath}|`)
          )
      ),
    menusList: orders.menusList,
    menuIndex: orders.menuIndex,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({}, dispatch);
export default connect(mapStateToProps, mapDispatchToProps)(React.memo(Item));
